import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { 
  Calendar as CalendarIcon, 
  Plus, 
  ChevronLeft,
  ChevronRight,
  Clock,
  Building2,
  Pencil,
  Trash2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITES = [
  { value: "basse", label: "Basse", color: "text-slate-500 bg-slate-50 border-slate-200" },
  { value: "normale", label: "Normale", color: "text-blue-600 bg-blue-50 border-blue-200" },
  { value: "haute", label: "Haute", color: "text-amber-600 bg-amber-50 border-amber-200" },
  { value: "urgente", label: "Urgente", color: "text-red-600 bg-red-50 border-red-200" }
];

const STATUTS = [
  { value: "a_faire", label: "À faire", color: "text-slate-500 bg-slate-50 border-slate-200" },
  { value: "en_cours", label: "En cours", color: "text-blue-600 bg-blue-50 border-blue-200" },
  { value: "termine", label: "Terminé", color: "text-emerald-600 bg-emerald-50 border-emerald-200" }
];

const initialFormData = {
  titre: "",
  description: "",
  chantier_id: "",
  chantier_nom_libre: "",
  type_intervention: "tache",
  date_debut: "",
  date_fin: "",
  statut: "a_faire",
  priorite: "normale"
};

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

export default function Calendrier() {
  const [taches, setTaches] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTache, setEditingTache] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tachesRes, chantiersRes] = await Promise.all([
        axios.get(`${API}/taches`),
        axios.get(`${API}/chantiers`)
      ]);
      setTaches(tachesRes.data);
      setChantiers(chantiersRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Build the payload — use chantier_nom_libre as title fallback for display
    const payload = {
      ...formData,
      // If no linked chantier_id, store the free-text name as chantier_id so it shows on the calendar
      chantier_id: formData.chantier_id || formData.chantier_nom_libre || "",
    };
    const typeLabel = { tache: "Tâche", intervention: "Intervention", reunion: "Réunion", livraison: "Livraison" };
    try {
      if (editingTache) {
        await axios.put(`${API}/taches/${editingTache.id}`, payload);
        toast.success("Mis à jour");
      } else {
        await axios.post(`${API}/taches`, payload);
        toast.success(`${typeLabel[formData.type_intervention] || "Tâche"} créée`);
      }
      setDialogOpen(false);
      setFormData(initialFormData);
      setEditingTache(null);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = (tache) => {
    setEditingTache(tache);
    const chantierNom = chantiers.find(c => c.id === tache.chantier_id)?.nom || tache.chantier_id || "";
    setFormData({ ...tache, chantier_nom_libre: chantierNom });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette tâche ?")) {
      try {
        await axios.delete(`${API}/taches/${id}`);
        toast.success("Tâche supprimée");
        fetchData();
      } catch (error) {
        toast.error("Erreur lors de la suppression");
      }
    }
  };

  const openNewDialog = (date = null) => {
    setEditingTache(null);
    const today = date || new Date().toISOString().split('T')[0];
    setFormData({...initialFormData, date_debut: today, date_fin: today});
    setDialogOpen(true);
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = (firstDay.getDay() + 6) % 7;
    
    const days = [];
    
    for (let i = startPadding - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, isCurrentMonth: false });
    }
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  }, [year, month]);

  const getTasksForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return taches.filter(t => t.date_debut <= dateStr && t.date_fin >= dateStr);
  };

  const getPrioriteStyle = (priorite) => PRIORITES.find(p => p.value === priorite)?.color || "";
  const getStatutStyle = (statut) => STATUTS.find(s => s.value === statut)?.color || "";
  const getStatutLabel = (statut) => STATUTS.find(s => s.value === statut)?.label || statut;
  const getChantierName = (id) => {
    if (!id) return "";
    const found = chantiers.find(c => c.id === id);
    return found ? found.nom : id; // fallback: id could be a free-text name
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <div className="w-8 h-8 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="calendrier-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1E293B] uppercase tracking-tight font-['Barlow_Condensed']">Calendrier</h1>
          <p className="text-[#94A3B8] mt-1">Planification des tâches</p>
        </div>
        <Button 
          data-testid="btn-new-tache"
          onClick={() => openNewDialog()}
          className="bg-[#3B82F6] hover:bg-blue-600 text-white rounded-lg uppercase tracking-wide text-sm font-medium btn-press shadow-md shadow-blue-200"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter
        </Button>
      </div>

      {chantiers.length === 0 ? (
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardContent className="p-12 text-center">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-[#CBD5E1]" />
            <h3 className="text-xl font-semibold text-[#1E293B] mb-2">Aucun chantier</h3>
            <p className="text-[#94A3B8]">Créez d'abord un chantier pour ajouter des tâches</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Calendar */}
          <Card className="xl:col-span-3 bg-white border-[#E2E8F0] shadow-sm">
            <CardHeader className="border-b border-[#F1F5F9]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-[#1E293B] flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-[#3B82F6]" />
                  {MONTHS[month]} {year}
                </CardTitle>
                <div className="flex gap-2">
                  <Button
                    data-testid="btn-prev-month"
                    variant="outline"
                    size="sm"
                    onClick={prevMonth}
                    className="border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9] rounded-lg h-8 w-8 p-0"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    data-testid="btn-next-month"
                    variant="outline"
                    size="sm"
                    onClick={nextMonth}
                    className="border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9] rounded-lg h-8 w-8 p-0"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {DAYS.map(day => (
                  <div key={day} className="text-center text-xs font-mono text-[#94A3B8] uppercase tracking-wider py-2">
                    {day}
                  </div>
                ))}
              </div>
              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, idx) => {
                  const dayTasks = getTasksForDate(day.date);
                  const dateStr = day.date.toISOString().split('T')[0];
                  return (
                    <div
                      key={idx}
                      data-testid={`calendar-day-${dateStr}`}
                      onClick={() => {
                        setSelectedDate(dateStr);
                        if (dayTasks.length === 0) openNewDialog(dateStr);
                      }}
                      className={`
                        min-h-[80px] p-1 rounded-lg border cursor-pointer transition-all
                        ${day.isCurrentMonth ? 'border-[#E2E8F0]' : 'border-transparent'}
                        ${isToday(day.date) ? 'bg-blue-50 border-[#3B82F6]' : ''}
                        ${selectedDate === dateStr ? 'ring-2 ring-[#3B82F6] ring-offset-1' : ''}
                        ${day.isCurrentMonth ? 'hover:bg-[#F8FAFC]' : 'opacity-40'}
                      `}
                    >
                      <div className={`text-sm font-semibold mb-1 ${isToday(day.date) ? 'text-[#3B82F6]' : day.isCurrentMonth ? 'text-[#1E293B]' : 'text-[#CBD5E1]'}`}>
                        {day.date.getDate()}
                      </div>
                      <div className="space-y-0.5">
                        {dayTasks.slice(0, 2).map(task => (
                          <div
                            key={task.id}
                            className={`text-xs truncate px-1 py-0.5 rounded border ${getPrioriteStyle(task.priorite)}`}
                          >
                            {task.titre}
                          </div>
                        ))}
                        {dayTasks.length > 2 && (
                          <div className="text-xs text-[#94A3B8]">+{dayTasks.length - 2} autres</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Task List */}
          <Card className="bg-white border-[#E2E8F0] shadow-sm">
            <CardHeader className="border-b border-[#F1F5F9] bg-gradient-to-r from-orange-50 to-transparent">
              <CardTitle className="text-lg font-semibold text-[#1E293B] flex items-center gap-2">
                <div className="p-2 rounded-lg bg-orange-100">
                  <Clock className="w-5 h-5 text-[#F97316]" />
                </div>
                Tâches à venir
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 max-h-[500px] overflow-y-auto">
              {taches.filter(t => t.statut !== 'termine').length === 0 ? (
                <div className="p-8 text-center text-[#94A3B8]">
                  <Clock className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>Aucune tâche en attente</p>
                </div>
              ) : (
                <div className="divide-y divide-[#F1F5F9]">
                  {taches
                    .filter(t => t.statut !== 'termine')
                    .sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut))
                    .map(tache => (
                      <div 
                        key={tache.id} 
                        data-testid={`tache-item-${tache.id}`}
                        className="p-3 hover:bg-[#F8FAFC] transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              {tache.type_intervention && tache.type_intervention !== "tache" && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white"
                                  style={{ background: { intervention: "#F97316", reunion: "#8B5CF6", livraison: "#10B981" }[tache.type_intervention] || "#3B82F6" }}>
                                  {{ intervention: "Intervention", reunion: "Réunion", livraison: "Livraison" }[tache.type_intervention]}
                                </span>
                              )}
                              <h4 className="font-medium text-[#1E293B] truncate">{tache.titre}</h4>
                            </div>
                            <p className="text-xs text-[#94A3B8] truncate">{getChantierName(tache.chantier_id)}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className={`px-1.5 py-0.5 rounded text-xs border ${getPrioriteStyle(tache.priorite)}`}>
                                {PRIORITES.find(p => p.value === tache.priorite)?.label}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded text-xs border ${getStatutStyle(tache.statut)}`}>
                                {getStatutLabel(tache.statut)}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              onClick={() => handleEdit(tache)}
                              className="p-1.5 text-[#94A3B8] hover:text-[#3B82F6] hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDelete(tache.id)}
                              className="p-1.5 text-[#94A3B8] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-[#E2E8F0] text-[#1E293B] max-w-lg shadow-xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-tight font-['Barlow_Condensed'] text-[#1E293B]">
              {editingTache ? "Modifier" : "Nouvelle entrée planning"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#475569] font-medium">Titre *</Label>
              <Input
                data-testid="input-titre-tache"
                required
                value={formData.titre}
                onChange={(e) => setFormData({...formData, titre: e.target.value})}
                className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg focus:ring-[#3B82F6]"
              />
            </div>
            {/* Type d'intervention */}
            <div className="space-y-2">
              <Label className="text-[#475569] font-medium">Type</Label>
              <div className="flex gap-2 flex-wrap">
                {[
                  { value: "tache", label: "Tâche", color: "#3B82F6" },
                  { value: "intervention", label: "Intervention", color: "#F97316" },
                  { value: "reunion", label: "Réunion", color: "#8B5CF6" },
                  { value: "livraison", label: "Livraison", color: "#10B981" },
                ].map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({...formData, type_intervention: type.value})}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all`}
                    style={formData.type_intervention === type.value
                      ? { background: type.color, color: "white", borderColor: type.color }
                      : { background: "#F8FAFC", color: "#64748B", borderColor: "#E2E8F0" }
                    }
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Chantier — combobox : liste existante OU saisie libre */}
            <div className="space-y-2">
              <Label className="text-[#475569] font-medium">Chantier</Label>
              <input
                list="chantiers-list"
                data-testid="select-chantier"
                value={formData.chantier_nom_libre}
                onChange={(e) => {
                  const val = e.target.value;
                  const found = chantiers.find(c => c.nom.trim() === val.trim());
                  setFormData({
                    ...formData,
                    chantier_nom_libre: val,
                    chantier_id: found ? found.id : ""
                  });
                }}
                placeholder="Sélectionner ou taper un nom de chantier..."
                className="w-full h-10 px-3 py-2 text-sm bg-[#F8FAFC] border border-[#E2E8F0] text-[#1E293B] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
              />
              <datalist id="chantiers-list">
                {chantiers.map(c => (
                  <option key={c.id} value={c.nom} />
                ))}
              </datalist>
              {formData.chantier_nom_libre && !chantiers.find(c => c.nom.trim() === formData.chantier_nom_libre.trim()) && (
                <p className="text-xs text-[#F97316]">Nouveau chantier (ne sera pas ajouté à la liste)</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#475569] font-medium">Date début *</Label>
                <Input
                  data-testid="input-date-debut-tache"
                  type="date"
                  required
                  value={formData.date_debut}
                  onChange={(e) => setFormData({...formData, date_debut: e.target.value})}
                  className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg focus:ring-[#3B82F6]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#475569] font-medium">Date fin *</Label>
                <Input
                  data-testid="input-date-fin-tache"
                  type="date"
                  required
                  value={formData.date_fin}
                  onChange={(e) => setFormData({...formData, date_fin: e.target.value})}
                  className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg focus:ring-[#3B82F6]"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#475569] font-medium">Priorité</Label>
                <Select value={formData.priorite} onValueChange={(v) => setFormData({...formData, priorite: v})}>
                  <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#E2E8F0] text-[#1E293B] shadow-lg">
                    {PRIORITES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#475569] font-medium">Statut</Label>
                <Select value={formData.statut} onValueChange={(v) => setFormData({...formData, statut: v})}>
                  <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#E2E8F0] text-[#1E293B] shadow-lg">
                    {STATUTS.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#475569] font-medium">Description</Label>
              <Textarea
                data-testid="input-description-tache"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg focus:ring-[#3B82F6] min-h-[80px]"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9] rounded-lg"
              >
                Annuler
              </Button>
              <Button
                data-testid="btn-submit-tache"
                type="submit"
                className="bg-[#3B82F6] hover:bg-blue-600 text-white rounded-lg shadow-md shadow-blue-200"
              >
                {editingTache ? "Mettre à jour" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
